import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, inject, provideBrowserGlobalErrorListeners } from '@angular/core';
import { provideRouter, withRouterConfig } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';

import { routes } from './app.routes';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { activeTenantResolutionInterceptor } from './core/interceptors/active-tenant-resolution.interceptor';
import { envAccessInterceptor } from './core/interceptors/env-access.interceptor';
import { KeycloakService } from './core/services/keycloak.service';
import { VersionService } from './core/services/version.service';
import { UserSettingsService } from './core/services/user-settings.service';
import { ThemeService } from './core/services/theme.service';
import { GlobalErrorHandlerService } from './core/services/global-error-handler.service';
import { pruneSortStorage } from './shared/utils/table-sort';

/** Bootstrap Keycloak before the Angular app renders. */
function initKeycloak() {
  return () => inject(KeycloakService).init();
}

/** Fetches backend version after Keycloak init; runs in parallel, failures handled in VersionService. */
function initVersion() {
  return () => inject(VersionService).load();
}

/** Loads settings before first render; injecting ThemeService here registers its bootstrap effect. load() never rejects. */
function initUserSettings() {
  return async () => {
    const kc           = inject(KeycloakService);
    const userSettings = inject(UserSettingsService);
    inject(ThemeService);            // registers the effect; return value not used

    await kc.init();                 // same Promise as initKeycloak - resolves immediately if done
    if (kc.authenticated()) {
      await userSettings.load();     // never rejects; falls back to defaults on error
    }
  };
}

/** Drops table-sort keys left behind by a renamed or removed table. */
function initSortStorage() {
  return () => pruneSortStorage();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
    { provide: GlobalErrorHandlerService, useExisting: ErrorHandler },
    provideRouter(routes, withRouterConfig({ onSameUrlNavigation: 'reload' })),
    // Order matters: authInterceptor attaches the JWT first, then the resolution
    // interceptor handles 412s; envAccessInterceptor runs last, after auth is resolved.
    provideHttpClient(withInterceptors([authInterceptor, activeTenantResolutionInterceptor, envAccessInterceptor])),
    {
      provide:    APP_INITIALIZER,
      useFactory: initKeycloak,
      multi:      true,
    },
    {
      provide:    APP_INITIALIZER,
      useFactory: initVersion,
      multi:      true,
    },
    {
      provide:    APP_INITIALIZER,
      useFactory: initUserSettings,
      multi:      true,
    },
    {
      provide:    APP_INITIALIZER,
      useFactory: initSortStorage,
      multi:      true,
    },
  ],
};
